/** A unique property to store the observer instance on the element, avoiding conflicts. */
const OBSERVER_PROP_NAME = '__cgVisibleObserver';
/**
 * Finds the closest scrollable ancestor of an element.
 *
 * @param element The element to find the scroll container for.
 * @returns The scrollable container or `null` if none is found (which defaults to the browser viewport).
 */
function findScrollContainer(element) {
    let parent = element.parentElement;
    while (parent != null) {
        const style = window.getComputedStyle(parent);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
            return parent;
        }
        parent = parent.parentElement;
    }
    return null;
}
/** A wrapper class around the IntersectionObserver to manage an element's visibility state. */
class VisibilityObserver {
    /** The HTML element being observed. */
    el;
    /** The callback to invoke on visibility change. */
    callback;
    /** The margin for the observer's root. */
    margin;
    /** Whether to stop observing after the first time the element becomes visible. */
    once;
    /** The native IntersectionObserver instance. */
    observer = null;
    /** The scroll container to use as the viewport, or `null` for the browser viewport. */
    scrollContainer;
    /** The last known visibility state of the element. */
    isVisible = undefined;
    /**
     * @param el The element to observe.
     * @param options The configuration options for the observer.
     */
    constructor(el, options) {
        this.el = el;
        this.callback = options.callback;
        this.margin = options.margin;
        this.once = options.once;
        this.scrollContainer = options.scrollContainer;
        this.createObserver();
    }
    /** Creates and starts the IntersectionObserver. */
    async createObserver() {
        // Ensure the element is mounted before creating the observer.
        await Promise.resolve();
        if (this.observer != null) {
            this.stop();
        }
        if (typeof this.callback !== 'function') {
            return;
        }
        try {
            this.observer = new IntersectionObserver(this.handler.bind(this), {
                root: this.scrollContainer,
                rootMargin: this.margin,
                threshold: 0,
            });
            this.observer.observe(this.el);
        }
        catch (e) {
            // Fallback for browsers that do not support IntersectionObserver.
            // eslint-disable-next-line no-console
            console.error('IntersectionObserver not supported.', e);
            this.observer = null;
            // A `null` value indicates that the visibility could not be determined.
            this.callback(null);
        }
    }
    /**
     * The handler that is called by the IntersectionObserver when visibility changes.
     *
     * @param entries The list of entries, typically containing just one for our observed element.
     */
    handler(entries) {
        const entry = entries[0];
        const isNowVisible = entry.isIntersecting || entry.intersectionRatio > 0;
        if (this.isVisible !== isNowVisible) {
            this.isVisible = isNowVisible;
            this.callback(this.isVisible);
            if (this.once && this.isVisible) {
                this.stop();
            }
        }
    }
    /** Stops observing the element and cleans up the observer. */
    stop() {
        if (this.observer != null) {
            this.observer.disconnect();
            this.observer = null;
        }
    }
}
/**
 * Stops and removes the observer instance from an element.
 *
 * @param el The element to clean up.
 */
function destroyObserver(el) {
    const observerInstance = el[OBSERVER_PROP_NAME];
    if (observerInstance instanceof VisibilityObserver) {
        observerInstance.stop();
        delete el[OBSERVER_PROP_NAME];
    }
}
/**
 * A Vue directive that triggers a callback when an element's visibility changes.
 *
 * Modifiers:
 * - `once`: The callback will only be triggered the first time the element becomes visible.
 * - `auto`: Automatically finds the nearest scrollable parent to use as the viewport.
 * - `[number]`: A number (e.g., `v-visible_200`) sets the root margin in pixels.
 *
 * @example
 * ```tsx
 * <div v-visible_once_200="onVisible">...</div>
 * ```
 */
export const visible = {
    /**
     * Called when the directive is first bound to the element.
     *
     * @param el The element the directive is bound to.
     * @param binding An object containing the directive's arguments, value, and modifiers.
     */
    bind(el, binding) {
        const { value: callback, modifiers } = binding;
        const options = {
            callback,
            margin: '0px',
            once: false,
            scrollContainer: null,
        };
        for (const mod in modifiers) {
            if (Object.hasOwn(modifiers, mod) && modifiers[mod]) {
                if (/^\d+$/.test(mod)) {
                    options.margin = `${mod}px`;
                }
                if (mod === 'once') {
                    options.once = true;
                }
                if (mod === 'auto') {
                    options.scrollContainer = findScrollContainer(el);
                }
            }
        }
        el[OBSERVER_PROP_NAME] = new VisibilityObserver(el, options);
    },
    /**
     * Called after the component has updated, to re-initialize the observer if needed.
     *
     * @param el The element the directive is bound to.
     * @param binding An object containing the directive's arguments, value, and modifiers.
     */
    componentUpdated(el, binding) {
        const { value, oldValue, modifiers } = binding;
        // Re-bind if the callback function or modifiers have changed.
        if (value !== oldValue || JSON.stringify(modifiers) !== JSON.stringify(binding.oldValue)) {
            destroyObserver(el);
            visible.bind(el, binding);
        }
    },
    /**
     * Called when the directive is unbound from the element.
     *
     * @param el The element to clean up.
     */
    unbind(el) {
        destroyObserver(el);
    },
};
